import React, { FC, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

// Layout Components
import { DashboardLayout, PageHeader } from '../components/layout';

// Feature Components
import { SeniorCard, AddSeniorCard, EditConfigModal, EditContactsModal, EditSeniorModal, DeleteSeniorModal, ActivationCodeModal } from '../components/features';

// UI Components
import { Button } from '../components/ui';

// Hooks
import { useSeniors, Senior } from '../hooks';

const ManageSeniors: FC = () => {
  const navigate = useNavigate();
  const { seniors, loading, refetch } = useSeniors();

  const currentUser = { name: 'Sarah Jensen' };

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // Edit config modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingSenior, setEditingSenior] = useState<Senior | null>(null);

  // Edit contacts modal state
  const [contactsModalOpen, setContactsModalOpen] = useState(false);
  const [contactsSenior, setContactsSenior] = useState<Senior | null>(null);

  // Edit senior modal state
  const [seniorModalOpen, setSeniorModalOpen] = useState(false);
  const [editingSeniorDetails, setEditingSeniorDetails] = useState<Senior | null>(null);

  // Delete senior modal state
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletingSenior, setDeletingSenior] = useState<Senior | null>(null);

  // Activation code modal state
  const [codeModalOpen, setCodeModalOpen] = useState(false);
  const [codeSenior, setCodeSenior] = useState<Senior | null>(null);

  // Filter seniors by search query
  const filteredSeniors = useMemo(() => {
    if (!searchQuery.trim()) return seniors;
    const query = searchQuery.toLowerCase();
    return seniors.filter(
      (senior) =>
        senior.name.toLowerCase().includes(query) ||
        (senior.device_name && senior.device_name.toLowerCase().includes(query)) ||
        (senior.activation_code && senior.activation_code.toLowerCase().includes(query))
    );
  }, [seniors, searchQuery]);

  // Modal handlers
  const handleEditConfig = (senior: Senior) => {
    setEditingSenior(senior);
    setEditModalOpen(true);
  };

  const handleEditContacts = (senior: Senior) => {
    setContactsSenior(senior);
    setContactsModalOpen(true);
  };

  const handleEditSenior = (senior: Senior) => {
    setEditingSeniorDetails(senior);
    setSeniorModalOpen(true);
  };

  const handleDeleteSenior = (senior: Senior) => {
    setDeletingSenior(senior);
    setDeleteModalOpen(true);
  };

  const handleViewCode = (senior: Senior) => {
    setCodeSenior(senior);
    setCodeModalOpen(true);
  };

  const handleLayoutUpdated = () => {
    refetch();
  };

  const handleContactsUpdated = () => {
    refetch();
  };

  return (
    <DashboardLayout currentUser={currentUser}>
      {/* Header */}
      <PageHeader
        title="Manage Seniors"
        subtitle={`${seniors.length} senior${seniors.length !== 1 ? 's' : ''} registered`}
        icon="diversity_1"
        action={
          <Button
            size="lg"
            onClick={() => navigate('/configure')}
            leftIcon={
              <span className="material-symbols-outlined text-[28px] group-hover:rotate-90 transition-transform font-bold">
                add_circle
              </span>
            }
          >
            Add New Senior
          </Button>
        }
      />

      {/* Search Bar */}
      <div className="relative">
        <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-brand-text-muted">
          search
        </span>
        <input
          type="text"
          placeholder="Search by name, device, or activation code..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-3 rounded-xl border border-brand-border bg-white text-brand-text placeholder-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-text-muted hover:text-brand-text transition-colors"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        )}
      </div>

      {/* Seniors Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              className="flex flex-col rounded-xl border border-brand-border bg-white p-4 sm:p-5 lg:p-6 animate-pulse h-[200px] sm:h-[260px] lg:h-[300px]"
            >
              <div className="flex items-center gap-3 sm:gap-4 lg:gap-5 mb-4 lg:mb-6">
                <div className="w-12 h-12 sm:w-16 sm:h-16 lg:w-20 lg:h-20 rounded-full bg-brand-bg-warm" />
                <div className="flex-1">
                  <div className="h-4 sm:h-5 lg:h-6 bg-brand-bg-warm rounded w-28 sm:w-32 mb-2" />
                  <div className="h-3 sm:h-4 bg-brand-bg rounded w-20 sm:w-24" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:gap-3 flex-1">
                <div className="bg-brand-bg rounded-lg sm:rounded-xl" />
                <div className="bg-brand-bg rounded-lg sm:rounded-xl" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredSeniors.length === 0 ? (
        <div className="text-center py-16">
          {searchQuery ? (
            <>
              <span className="material-symbols-outlined text-5xl text-brand-text-muted mb-4 block">
                search_off
              </span>
              <h3 className="text-lg font-display font-bold text-brand-text mb-2">
                No results found
              </h3>
              <p className="text-brand-text-muted">
                No seniors match "{searchQuery}". Try a different search term.
              </p>
            </>
          ) : (
            <>
              <span className="material-symbols-outlined text-5xl text-brand-text-muted mb-4 block">
                person_add
              </span>
              <h3 className="text-lg font-display font-bold text-brand-text mb-2">
                No seniors yet
              </h3>
              <p className="text-brand-text-muted mb-6">
                Add your first senior to get started.
              </p>
              <Button onClick={() => navigate('/configure')}>
                Add New Senior
              </Button>
            </>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Compact list view */}
          <div className="flex flex-col gap-2 sm:hidden">
            {filteredSeniors.map((senior) => (
              <SeniorCard
                key={senior.id}
                name={senior.name}
                deviceName={senior.device_name || 'Unknown Device'}
                profileType={senior.profile_type || 'cognitive'}
                status={senior.status}
                battery={senior.battery}
                lastSeen={senior.last_seen}
                activationCode={senior.activation_code}
                onEditConfig={() => handleEditConfig(senior)}
                onEditSenior={() => handleEditSenior(senior)}
                onDelete={() => handleDeleteSenior(senior)}
                onViewCode={() => handleViewCode(senior)}
                compact
              />
            ))}
            <AddSeniorCard onClick={() => navigate('/configure')} compact />
          </div>

          {/* Tablet+: Card grid view */}
          <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {filteredSeniors.map((senior) => (
              <SeniorCard
                key={senior.id}
                name={senior.name}
                deviceName={senior.device_name || 'Unknown Device'}
                profileType={senior.profile_type || 'cognitive'}
                status={senior.status}
                battery={senior.battery}
                lastSeen={senior.last_seen}
                activationCode={senior.activation_code}
                onViewActivity={() => {}}
                onEditConfig={() => handleEditConfig(senior)}
                onEditContacts={() => handleEditContacts(senior)}
                onEditSenior={() => handleEditSenior(senior)}
                onDelete={() => handleDeleteSenior(senior)}
                onViewCode={() => handleViewCode(senior)}
              />
            ))}
            <AddSeniorCard onClick={() => navigate('/configure')} />
          </div>
        </>
      )}

      {/* Search results count */}
      {!loading && searchQuery && filteredSeniors.length > 0 && (
        <p className="text-sm text-brand-text-muted text-center">
          Showing {filteredSeniors.length} of {seniors.length} senior{seniors.length !== 1 ? 's' : ''}
        </p>
      )}

      {/* Edit Config Modal */}
      <EditConfigModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingSenior(null);
        }}
        seniorId={editingSenior?.id || ''}
        seniorName={editingSenior?.name || ''}
        currentLayoutId={editingSenior?.layout_id}
        onLayoutUpdated={handleLayoutUpdated}
      />

      {/* Edit Contacts Modal */}
      <EditContactsModal
        isOpen={contactsModalOpen}
        onClose={() => {
          setContactsModalOpen(false);
          setContactsSenior(null);
        }}
        seniorId={contactsSenior?.id || ''}
        seniorName={contactsSenior?.name || ''}
        onContactsUpdated={handleContactsUpdated}
      />

      {/* Edit Senior Modal */}
      <EditSeniorModal
        isOpen={seniorModalOpen}
        onClose={() => {
          setSeniorModalOpen(false);
          setEditingSeniorDetails(null);
        }}
        seniorId={editingSeniorDetails?.id || ''}
        seniorName={editingSeniorDetails?.name || ''}
        profileType={editingSeniorDetails?.profile_type}
        severity={editingSeniorDetails?.severity}
        deviceName={editingSeniorDetails?.device_name}
        onSeniorUpdated={() => refetch()}
        onSeniorDeleted={() => refetch()}
      />

      {/* Delete Senior Modal */}
      <DeleteSeniorModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setDeletingSenior(null);
        }}
        seniorId={deletingSenior?.id || ''}
        seniorName={deletingSenior?.name || ''}
        onDeleted={() => refetch()}
      />

      {/* Activation Code Modal */}
      <ActivationCodeModal
        isOpen={codeModalOpen}
        onClose={() => {
          setCodeModalOpen(false);
          setCodeSenior(null);
        }}
        seniorName={codeSenior?.name || ''}
        seniorId={codeSenior?.id || ''}
        activationCode={codeSenior?.activation_code || ''}
        onCodeRegenerated={() => refetch()}
      />
    </DashboardLayout>
  );
};

export default ManageSeniors;
